/**
 * 外部SA 管理画面 (/sales/sa-management)。
 *
 * 招待済みの外部セールスエージェント (admins: dept='sales' && level='external') を一覧し、
 * オンボーディングのステータス (仮登録 → 登録完了 → 活動可能) を進行させる。
 *
 * - 同意済み / 入金済み が揃うと「登録完了」+ 契約開始日 (翌月1日) を自動設定。
 * - 受講済みで「活動可能」へ。各遷移で SA に自動メール (sa_registered / sa_active)。
 * - 「メール作成」で手動メール送信。「契約日」で開始日の手動上書き。
 *
 * UI/UX は「メール配信先 (配信先管理)」を踏襲。owner 限定 (admins read 制約)。
 * 仕様: project_sa_management_screen
 */
import {
  SA_ONBOARDING_STATUS_LABEL,
  type SaOnboardingStatus,
} from '@goenhub/shared';
import {
  Banknote,
  CalendarClock,
  Check,
  FileSignature,
  GraduationCap,
  Mail,
  RotateCw,
  Search,
  Settings2,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';


import { SaContractDialog } from './SaContractDialog';
import { SaMailDialog } from './SaMailDialog';
import { SaRetentionSettingsTab } from './SaRetentionSettingsTab';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useMyPermissions } from '@/features/permissions';
import {
  useRenewSaContract,
  useSaAgents,
  useUpdateSaOnboarding,
  type SaAgentRecord,
} from '@/hooks/useSaAgents';
import { formatDate } from '@/lib/firestoreTime';

const STATUS_BADGE: Record<
  SaOnboardingStatus,
  'draft' | 'review' | 'approved'
> = {
  provisional: 'draft',
  registered: 'review',
  active: 'approved',
};

type FlagField = 'consentConfirmed' | 'paymentConfirmed' | 'briefingAttended';

export function SaManagementPage() {
  const { isOwner } = useMyPermissions();
  const { data: agents, isLoading } = useSaAgents();
  const updateMutation = useUpdateSaOnboarding();
  const renewMutation = useRenewSaContract();

  const [keyword, setKeyword] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [mailTarget, setMailTarget] = useState<SaAgentRecord | null>(null);
  const [contractTarget, setContractTarget] = useState<SaAgentRecord | null>(null);

  const filtered = useMemo(() => {
    const list = agents ?? [];
    const k = keyword.trim().toLowerCase();
    if (!k) return list;
    return list.filter(
      (a) =>
        (a.fullName ?? a.displayName ?? '').toLowerCase().includes(k) ||
        a.email.toLowerCase().includes(k),
    );
  }, [agents, keyword]);

  if (!isOwner) {
    return (
      <EmptyState
        icon={Users}
        title="アクセス権限がありません"
        description="SA 管理は owner 権限のユーザーのみ利用できます。"
      />
    );
  }

  const toggleFlag = async (agent: SaAgentRecord, field: FlagField) => {
    const key = `${agent.uid}:${field}`;
    setBusyKey(key);
    const current = agent.saOnboarding?.[field] ?? false;
    try {
      await updateMutation.mutateAsync({
        uid: agent.uid,
        patch: { [field]: !current },
      });
    } catch (err) {
      toast.error(
        `更新に失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusyKey(null);
    }
  };

  const handleRenew = async (agent: SaAgentRecord) => {
    const name = agent.fullName || agent.displayName || agent.email;
    if (
      !window.confirm(
        `${name} の契約を次期へ更新します。\n同意・入金・受講がリセットされ、再度オンボーディング（更新料の入金確認・再受講）が必要になります。\nよろしいですか？`,
      )
    ) {
      return;
    }
    const key = `${agent.uid}:renew`;
    setBusyKey(key);
    try {
      const res = await renewMutation.mutateAsync(agent.uid);
      toast.success(
        `第${res.contractTerm}期へ更新しました（${res.contractStartDate} 〜 ${res.contractEndDate}）`,
      );
    } catch (err) {
      toast.error(
        `更新に失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Screen
      title="SA 管理"
      description="外部セールスエージェントの登録〜活動可能までのステータスを管理します。"
    >
      <Tabs defaultValue="agents">
        <TabsList>
          <TabsTrigger value="agents">
            <Users className="size-3.5 mr-1" />
            エージェント
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings2 className="size-3.5 mr-1" />
            設定
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agents">
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
          <Input
            type="search"
            placeholder="名前 / メールアドレスで検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)] mb-2">
        {isLoading ? '読み込み中…' : `${filtered.length} 名 / 全 ${agents?.length ?? 0} 名`}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={keyword ? '一致する SA がいません' : 'SA がまだ登録されていません'}
          description={
            keyword
              ? 'キーワードを変更してください'
              : 'ユーザー招待で外部SA (営業部・外部) を招待すると、ここに仮登録で表示されます。'
          }
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {filtered.map((agent) => {
            const ob = agent.saOnboarding;
            const status = agent.saStatus;
            const name = agent.fullName || agent.displayName || agent.email;
            const reachedRegistered = status !== 'provisional';
            const rowBusy = busyKey?.startsWith(`${agent.uid}:`) ?? false;

            return (
              <div
                key={agent.uid}
                className="flex items-center gap-3 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
              >
                {agent.photoUrl ? (
                  <img
                    src={agent.photoUrl}
                    alt=""
                    className="size-8 rounded-full flex-shrink-0"
                  />
                ) : (
                  <div className="size-8 rounded-full bg-[var(--bg-hover)] flex items-center justify-center text-xs flex-shrink-0">
                    {name.charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{name}</div>
                  <div className="text-xs text-[var(--text-muted)] truncate">
                    {agent.email}
                  </div>
                </div>

                {/* 日付 (申請 / 契約期間) */}
                <div className="hidden lg:flex flex-col text-[11px] text-[var(--text-muted)] text-right leading-tight min-w-32">
                  <span>申請: {formatDate(ob?.appliedAt ?? agent.invitedAt)}</span>
                  <span>
                    {ob?.contractStartDate ?? '—'} 〜 {ob?.contractEndDate ?? '—'}
                    {ob?.contractTerm ? ` (第${ob.contractTerm}期)` : ''}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Badge variant={STATUS_BADGE[status]}>
                    {SA_ONBOARDING_STATUS_LABEL[status]}
                  </Badge>

                  {/* 同意済み トグル */}
                  <Button
                    type="button"
                    variant={ob?.consentConfirmed ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => toggleFlag(agent, 'consentConfirmed')}
                    disabled={rowBusy}
                    title={ob?.consentConfirmed ? '同意済み (クリックで取消)' : '同意書を確認 → 同意済みにする'}
                  >
                    {ob?.consentConfirmed ? (
                      <Check className="size-3.5" />
                    ) : (
                      <FileSignature className="size-3.5" />
                    )}
                    同意
                  </Button>

                  {/* 入金済み トグル */}
                  <Button
                    type="button"
                    variant={ob?.paymentConfirmed ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => toggleFlag(agent, 'paymentConfirmed')}
                    disabled={rowBusy}
                    title={ob?.paymentConfirmed ? '入金済み (クリックで取消)' : '入金を確認 → 入金済みにする'}
                  >
                    {ob?.paymentConfirmed ? (
                      <Check className="size-3.5" />
                    ) : (
                      <Banknote className="size-3.5" />
                    )}
                    入金
                  </Button>

                  {/* 受講: 登録完了後のみ。未受講/受講済みラベル + トグル */}
                  {reachedRegistered && (
                    <>
                      <Badge variant={ob?.briefingAttended ? 'sent' : 'default'}>
                        {ob?.briefingAttended ? '受講済み' : '未受講'}
                      </Badge>
                      <Button
                        type="button"
                        variant={ob?.briefingAttended ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => toggleFlag(agent, 'briefingAttended')}
                        disabled={rowBusy}
                        title={ob?.briefingAttended ? '受講済み (クリックで取消)' : '説明会の受講を確認 → 受講済みにする'}
                      >
                        <GraduationCap className="size-3.5" />
                        受講
                      </Button>
                    </>
                  )}

                  {/* 更新申請ありの表示 (active のみ) */}
                  {status === 'active' && ob?.renewalRequestedAt && (
                    <Badge variant="accent">更新申請</Badge>
                  )}

                  {/* 契約更新 (active のみ): 次期へ。リセットして再オンボーディング */}
                  {status === 'active' && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRenew(agent)}
                      disabled={rowBusy}
                      title="契約を次期へ更新（同意・入金・受講をリセット）"
                    >
                      <RotateCw className="size-3.5" />
                      更新
                    </Button>
                  )}

                  {/* 契約日編集 */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setContractTarget(agent)}
                    title="契約日を編集"
                  >
                    <CalendarClock className="size-4" />
                  </Button>

                  {/* メール作成 */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setMailTarget(agent)}
                    title="SA へメール作成"
                  >
                    <Mail className="size-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SaMailDialog
        open={!!mailTarget}
        onOpenChange={(o) => !o && setMailTarget(null)}
        agent={mailTarget}
      />
      <SaContractDialog
        open={!!contractTarget}
        onOpenChange={(o) => !o && setContractTarget(null)}
        agent={contractTarget}
      />
        </TabsContent>

        <TabsContent value="settings">
          <SaRetentionSettingsTab />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}
